import { makeAutoObservable } from "mobx";
import {
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
  extractSectionWithMaxScoreFromArticles,
} from "@/dbot/common/commonDomain";

import { Language } from "@/i18n/languages";
import { LocalizedText } from "@/common/common";
import { DecisionType } from "@/common/decisions";
import {
  ArticleSelectorModel,
  initArticleTextModels,
  LocalizedStringFieldModel,
} from "@/common/commonForms";

export type ChangeSignatureInArticlesOfAssociation = {
  type: DecisionType.changeSignatureInArticlesOfAssociation;
  paragraph?: number;
  texts?: LocalizedText[];
};

export class ChangeSignatureInArticlesOfAssociationForm {
  readonly type = DecisionType.changeSignatureInArticlesOfAssociation;
  readonly data?: ChangeSignatureInArticlesOfAssociation;
  paragraph: ArticleSelectorModel;
  texts: LocalizedStringFieldModel[];
  dispatch: DbotEventDispatcher;

  constructor(
    dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<ChangeSignatureInArticlesOfAssociation>,
    data?: ChangeSignatureInArticlesOfAssociation,
  ) {
    makeAutoObservable(this, { type: false, data: false }, { autoBind: true });
    this.paragraph = new ArticleSelectorModel(context.inputState?.articles, data?.paragraph);
    this.texts = initArticleTextModels({ selectorModel: this.paragraph, value: data?.texts });
    this.data = data;
    this.dispatch = dispatch;
  }

  init(): void {
    if (!this.paragraph.selected) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent): void {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success":
        if (!this.paragraph.selected) {
          this.setParagraphValue(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SIGNATURE"),
          );
        }
        break;
    }
  }

  get languages() {
    return this.context.inputState?.articles?.languages ?? [Language.NO];
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return Array.from(this.texts.values()).every((t) => t.model.isValid) && this.paragraph.isValid;
  }

  setParagraphValue(newValue: number | undefined) {
    this.paragraph.setSelected(newValue);
    this.texts = initArticleTextModels({ selectorModel: this.paragraph });
  }

  validate(): boolean {
    for (const t of this.texts.values()) {
      t.model.validate();
    }
    return this.isValid;
  }
}
